import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
	faTrashAlt,
	faClose,
	faChevronDown,
	faLink,
} from '@fortawesome/free-solid-svg-icons'

import PopoverButton from '../PopoverButton/index.vue'
import { currencyFormat } from '@/helpers/currency-format'

library.add(faTrashAlt, faClose, faChevronDown, faLink)

@Component({
	components: {
		PopoverButton,
	},
})
export default class CPerse extends Vue {
	@Prop({ default: '#aaa', type: String }) color!: string
	@Prop({ required: true, type: String }) id!: string
	@Prop({ required: true, type: Number }) infoId!: number

	form = {
		aliquot: {
			noacc: {
				pis: 1.65,
				cofins: 7.6,
				irpj: 25,
				csll: 9,
			},
			acc: {
				pis: 0.65,
				cofins: 3,
				irpj: 25,
				csll: 9,
			},
		},
		regime: 'noacc',
		months: 2,
		monthlyIncome: '',
		profit: '',
		profitPeriod: 'Mensal',
	}

	advanced = {
		selic: 1.25,
	}

	options = {
		regime: [
			{ text: 'Não cumulativo', value: 'noacc' },
			{ text: 'Cumulativo', value: 'acc' },
		],
		period: ['Mensal', 'Anual'],
	}

	money = {
		decimal: ',',
		thousands: '.',
		precision: 2,
		masked: false,
	}

	get realProfit() {
		let profit = parseFloat(
			this.form.profit.replaceAll('.', '').replaceAll(',', '.'),
		)
		if (this.form.profitPeriod === 'Anual') profit = profit / 12

		return profit
	}

	get pisSavings() {
		const aliquot = this.form.aliquot[this.form.regime].pis / 100
		const monthlyIncome = parseFloat(
			this.form.monthlyIncome.replaceAll('.', '').replaceAll(',', '.'),
		)

		return monthlyIncome * aliquot
	}

	get cofinsSavings() {
		const aliquot = this.form.aliquot[this.form.regime].cofins / 100

		return this.realProfit * aliquot
	}

	get irpjSavings() {
		if (!this.realProfit) return 0
		const aliquot = this.form.aliquot[this.form.regime].irpj / 100

		return 2000 + (this.realProfit - 20000) * aliquot
	}

	get csllSavings() {
		const aliquot = this.form.aliquot[this.form.regime].csll / 100
		return this.realProfit * aliquot
	}

	get monthlySavings() {
		const result = this.parse(
			(this.pisSavings +
				this.cofinsSavings +
				this.irpjSavings +
				this.csllSavings) *
				this.form.months,
		)
		this.$emit('savings', result)

		return currencyFormat(result)
	}

	get recuperateOn() {
		const result = this.parse(
			(this.pisSavings +
				this.cofinsSavings +
				this.irpjSavings +
				this.csllSavings) *
				(60 - this.form.months),
		)
		this.$emit('recuperate', result)

		return currencyFormat(result)
	}

	get selectedRegime() {
		const option = this.options.regime.find((option) => {
			return this.form.regime === option.value
		})

		return option ? option.text : ''
	}

	get leftMonths() {
		return 60 - this.form.months
	}

	get calcsEach() {
		return this.$store.state.calculators.calcsEach
	}

	get calcsPrint() {
		return this.$store.state.calculators.calcsPrint
	}

	currencyFormat(n) {
		return currencyFormat(n)
	}

	parse(n) {
		if (isNaN(n)) return 0
		n = parseFloat(n)
		return parseFloat(n.toFixed(2))
	}

	save() {
		this.savePrint()
		const found = this.calcsEach.find((item) => this.id === item.id)

		if (found) {
			found.aliquot.noacc = this.form.aliquot.noacc
			found.aliquot.acc = this.form.aliquot.acc
			found.regime = this.form.regime
			found.months = this.form.months
			found.monthlyIncome = this.form.monthlyIncome
			found.profit = this.form.profit
			found.profitPeriod = this.form.profitPeriod
			found.selic = this.advanced.selic
		} else {
			this.calcsEach.unshift({
				id: this.id,
				aliquot: {
					noacc: this.form.aliquot.noacc,
					acc: this.form.aliquot.acc,
				},
				regime: this.form.regime,
				months: this.form.months,
				monthlyIncome: this.form.monthlyIncome,
				profit: this.form.profit,
				profitPeriod: this.form.profitPeriod,
				selic: this.advanced.selic,
			})
		}
	}

	savePrint() {
		const found = this.calcsPrint.find((item) => this.id === item.id)

		const rows = () => {
			return [
				[
					{
						label: 'Faturamento Mensal',
						value: 'R$ ' + this.form.monthlyIncome,
					},
					{
						label: 'Lucro ' + this.form.profitPeriod,
						value: this.form.profit,
					},
					{
						label: 'Regime',
						value: this.selectedRegime,
					},
				],
				[
					{
						label:
							'Recuperação - ' +
							this.form.months +
							' meses após março de 2022',
						value: 'R$ ' + this.monthlySavings,
					},
					{
						label:
							'Economia - ' +
							this.leftMonths +
							' meses até março de 2027',
						value: 'R$ ' + this.recuperateOn,
					},
				],
			]
		}

		if (found) {
			found.rows = rows()
			found.recuperate = this.recuperateOn
			found.savings = this.monthlySavings
		} else {
			this.calcsPrint.unshift({
				id: this.id,
				title: 'Direito à alíquota zero - PERSE',
				color: this.color,
				columns: 3,
				rows: rows(),
				type: 'table',
				recuperate: this.recuperateOn,
				savings: this.monthlySavings,
			})
		}
	}

	created() {
		const found = this.calcsEach.find((item) => this.id === item.id)

		if (found) {
			this.form.aliquot.noacc = found.aliquot.noacc
			this.form.aliquot.acc = found.aliquot.acc
			this.form.regime = found.regime
			this.form.months = found.months
			this.form.monthlyIncome = found.monthlyIncome
			this.form.profit = found.profit
			this.form.profitPeriod = found.profitPeriod
			this.advanced.selic = found.selic
		}
	}
}
